/* eslint-disable */

import Vue from 'vue';
import pagesService from '../../api/pages-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '../../util/event-bus';
import i18n from '../../i18n/i18n-config';
import { removeArrayItem } from '../../util/array';
import { mapErrorsToInputs, scrollToFirstError } from '../../util/forms';
import imageService from '../../api/image-service';

function updateTreeRecursively(branch, updatedPage, branchParentId, updatedPageOldParentId) {
  for (let i = 0; i < branch.length; i++) {
    const page = branch[i];

    if (page.id === updatedPage.id) {
      // current branch contains updated page
      if (page.parent_id === updatedPage.parent_id) {
        // update it if it wasn't moved and return from the function
        branch.splice(i, 1, updatedPage);
        return branch;
      }

      // otherwise remove it from it's old parent and keep looking,
      // we still need to push it to it's new parent
      branch.splice(i, 1);
      i--;
    }

    // go deeper inside the tree
    page.child_pages = updateTreeRecursively(
      page.child_pages || [],
      updatedPage,
      page.id,
      updatedPageOldParentId,
    );
  }

  if (
    branchParentId === updatedPage.parent_id &&
    updatedPageOldParentId !== updatedPage.parent_id
  ) {
    // if page's parent changed to current branch, add it to it
    branch.push(updatedPage);
    // and sort branch by id, because that's how it is currently in backend
    // we want the tree to stay consistent after page refresh
    branch.sort((a, b) => a.id > b.id);
  }

  return branch;
}

function deletePageRecursively(branch, deletedPage) {
  for (let i = 0; i < branch.length; i++) {
    const page = branch[i];

    if (page.id === deletedPage.id) {
      branch.splice(i, 1);
      return branch;
    }

    page.child_pages = deletePageRecursively(page.child_pages || [], deletedPage);
  }

  return branch;
}

const state = {
  pages: null, // tree (branches = child_pages)
  editedPage: {},
  pagesValidationErrors: {},
  loadingPages: false,
  savingPage: false,
  savingImages: false,
  oldParentId: null, // for tree update purposes
};

const getters = {
  pagesAutocomplete(state) {
    return state.pages
      .filter((page) => page.title && page.id !== state.editedPage.id)
      .map((page) => ({ ...page, title: `${page.title} (${i18n.t(page.type)})` }))
      .filter((page) => page.type === state.editedPage.type);
  },
};

const mutations = {
  SET_PAGES(state, data) {
    state.pages = data;
  },

  STORE_PAGE(state, page) {
    state.pages = [...state.pages, page];
    state.pagesValidationErrors = {};
  },

  SET_EDITED_PAGE(state, page) {
    state.pagesValidationErrors = {};
    state.editedPage = JSON.parse(JSON.stringify(page));
    state.oldParentId = page.parent_id;
  },

  UPDATE_PAGE(state, page) {
    state.pages = updateTreeRecursively(state.pages, page, null, state.oldParentId);
  },

  DELETE_PAGE(state, page) {
    state.pages = deletePageRecursively(state.pages, page);
  },

  SET_PAGES_VALIDATION_ERRORS(state, validationError) {
    state.pagesValidationErrors = validationError;
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.pagesValidationErrors[field];
  },

  SET_LOADING_PAGES(state, loading) {
    state.loadingPages = loading;
  },

  SET_SAVING_PAGES(state, saving) {
    state.savingPage = saving;
  },

  SET_SAVING_IMAGES(state, saving) {
    state.savingImages = saving;
  },

  SET_PAGES_IMAGES(state, { images, type }) {
    Vue.set(state.editedPage, type, [...state.editedPage[type], ...images]);
    for (let i = 0; i < state.pages?.length; i++) {
      if (state.pages[i].id === +images.model_id) {
        Vue.set(state.pages[i], type, [...state.pages[i][type], ...images]);
        break;
      }
    }
  },

  DELETE_PAGES_IMAGE(state, { image, type }) {
    Vue.set(state.editedPage, type, removeArrayItem(state.editedPage[type], image));
    for (let i = 0; i < state.pages?.length; i++) {
      if (state.pages[i].id === +image.model_id) {
        Vue.set(state.pages[i], type, removeArrayItem(state.pages[i][type], image));
        break;
      }
    }
  },

  SET_PAGE_PDF_REPORT(state, file) {
    state.editedPage.pdf_report = { ...file[0] };
  },

  DELETE_PAGE_PDF_REPORT(state, file) {
    if (state.editedPage.pdf_report && state.editedPage.pdf_report.id === file.id) {
      state.editedPage.pdf_report = null;
    }
  },
};

const actions = {
  fetchPages({ commit }, params) {
    commit('SET_LOADING_PAGES', true);
    return pagesService
      .getPage(params)
      .then((res) => {
        commit('SET_PAGES', res.data);
      })
      .finally(() => {
        commit('SET_LOADING_PAGES', false);
      });
  },

  async storePage({ commit }) {
    commit('SET_SAVING_PAGES', true);
    const res = await pagesService.create();
    commit('SET_SAVING_PAGES', false);
    commit('STORE_PAGE', res.data);
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('page_created'));
    return res;
  },

  editPage({ state, commit }, pageId) {
    const page = state.pages?.find((p) => p.id === pageId);
    if (page) {
      commit('SET_EDITED_PAGE', page);
      return Promise.resolve(page);
    }
    return pagesService.getById(pageId).then((res) => {
      commit('SET_EDITED_PAGE', res.data);
    });
  },

  updatePage({ commit }, page) {
    commit('SET_SAVING_PAGES', true);
    const requestBody = {
      ...page,
      parent_id: page.parent_id || null,
    };
    return pagesService
      .update(requestBody)
      .then((res) => {
        commit('UPDATE_PAGE', res.data);
      })
      .catch((err) => {
        commit('SET_PAGES_VALIDATION_ERRORS', mapErrorsToInputs(err));
        scrollToFirstError();
        throw err;
      })
      .finally(() => commit('SET_SAVING_PAGES', false));
  },

  deletePage({ commit }, page) {
    openConfirmDialog({
      title: i18n.t('confirm_page_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      pagesService.delete(page).then(() => {
        commit('DELETE_PAGE', page);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('page_deleted'));
      });
    });
  },

  async uploadPagesImages({ commit }, { images, type, modelType, modelId }) {
    commit('SET_SAVING_IMAGES', true);
    eventBus.$emit(OPEN_SNACKBAR, {
      text: i18n.t('uploading'),
      showProgress: true,
      timeout: -1,
    });
    const res = await imageService.upload(images, type, modelType, modelId);
    commit('SET_SAVING_IMAGES', false);
    commit('SET_PAGES_IMAGES', { images: res.data, type: `${type}s` });
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('images_uploaded'));
    return res;
  },

  deletePagesImages({ commit }, image) {
    openConfirmDialog({
      title: i18n.t('confirm_image_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      imageService.delete(image).then(() => {
        commit('DELETE_PAGES_IMAGE', { image, type: `${image.type}s` });
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('image_deleted'));
      });
    });
  },

  async uploadPagePdfReport({ commit }, { images, type, modelType, modelId }) {
    commit('SET_SAVING_IMAGES', true);
    eventBus.$emit(OPEN_SNACKBAR, {
      text: i18n.t('uploading'),
      showProgress: true,
      timeout: -1,
    });
    const res = await imageService.upload(images, type, modelType, modelId);
    commit('SET_SAVING_IMAGES', false);
    commit('SET_PAGE_PDF_REPORT', res.data);
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('pdf_report_uploaded'));
    return res;
  },

  deletePagePdfReport({ commit }, file) {
    openConfirmDialog({
      title: i18n.t('confirm_pdf_report_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      imageService.delete(file).then(() => {
        commit('DELETE_PAGE_PDF_REPORT', file);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('pdf_report_deleted'));
      });
    });
  },

  clearPagesValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
